
import { WebSocketController } from "@/control/socket";
import { UserMediaController } from "./user-media";
import { Request } from "@asanrom/request-browser";
import { Peer } from "peerjs";
import { fetchFromLocalStorageCache, saveIntoLocalStorage } from "@/utils/local-storage";
import { AppPreferences } from "./app-preferences";
import { ApiCalls } from "@/api/api-group-calls";
import { AppEvents } from "./app-events";
import { ExitPreventer } from "@/utils/exit-preventer";
import { getLanguage } from "@/i18n";
import { RoomController } from "./room";
import { AsyncSemaphore } from "@asanrom/async-tools";
import { isCallEventForScope, normalizeCallScopeId, shouldHydrateSnapshotParticipant } from "./call-scope.js";

const BLOB_SIZE_THRESHOLD = 0;

function $t(s: string): string {
    return s;
}

/**
 * Calls controller
 * 
 * Depends on WebSocketController
 * 
 * Handles calls, using websockets
 * and WebRTC
 * 
 * Events:
 *  - 'open' (id, stream, userInfo: {name, image}, muted) - Emits when you join the call
 *  - 'close' - Emits when you leave the call
 * 
 *  - 'join-code-change' (code) - Emits when call code changes (for private calls)
 * 
 *  - 'self-mute' (muted) - Emits when mute status changes for yourself
 *  - 'self-change' (video, audio, hand) - Emits when preferences are changed
 * 
 *  - 'user-join' (id, userInfo: {name, image}, muted, video, audio, flags, hand)
 *  - 'user-leave' (id)
 *  - 'user-change' (id, muted, video, audio, hand)
 *  - 'user-stream' (id, stream)
 *  - 'user-volume' (id, volume)
 * 
 *  - 'screen-share-start' (isSelf, uid, userInfo: {name, image})
 *  - 'screen-share-stream' (uid, stream)
 *  - 'screen-share-end' (uid)
 * 
 *  - 'error-msg' (title, message) - Emits to warn the user about an error
 */

export class CallsController {

    public static room = "";
    public static joinedCall = false;
    private static events: { [key: string]: Array<(...args: any[]) => void> } = {};

    public static rejoinTimeout = null;
    public static rejoinCode = "";
    public static closed = true;

    // Breakout transitions are driven by room-scoped websocket events.  The
    // latest requested code wins while a PeerJS connection is being rebuilt.
    private static breakoutSwitching = false;
    private static breakoutAttemptCode = "";
    private static breakoutDesiredCode: string | null = null;
    private static breakoutRestoreVideo = false;
    private static breakoutFallbackUsed = false;
    /** Raw ids for replacement leave acknowledgements that must not close the new call. */
    private static pendingReplacementCallIds: string[] = [];

    public static callUsers = {};
    public static pendingCalls = {};
    public static pendingScreenCalls = {};

    public static preferences = {
        audio: true,
        video: true,
    };

    public static peer = null;
    public static peerID = "";

    /** Raw call id used by the HTTP API and websocket protocol. Empty means the main call. */
    public static callId = "";
    /** Stable internal scope. The main call uses a sentinel instead of an empty id. */
    public static callScopeId = "";
    /** PeerJS may be connected before the websocket has acknowledged call-open. */
    public static callOpen = false;
    public static callCode = "";
    public static breakoutCall = false;

    public static audio = true;
    public static video = true;
    public static hand = false;
    public static audioMode = false;
    public static modMuted = false;
    public static micAvailable = true;

    public static startRecordingUser = false;

    /** AudioContext + OscillatorNode created in the silent-mic fallback path.
     *  Stored so destroyCallData() can clean them up. */
    public static silentCtx: AudioContext | null = null;
    public static silentOscillator: OscillatorNode | null = null;

    public static selfUserStream = null;
    public static selfUserStreamAudio = null;
    public static selfUserStreamVideo = null;
    // Silent canvas video track added to selfUserStream at join time so the initial
    // SDP offer always has a video m-line.  This lets replaceTrack() swap in/out
    // the real camera track later without any SDP renegotiation (PeerJS does not
    // support renegotiation after the initial handshake).
    public static dummyVideoTrack: MediaStreamTrack | null = null;
    public static selfId = "";
    public static selfName = "";
    public static selfImage = "";

    public static screenPeer = null;
    public static screenPeerId = "";

    public static screenShareData = {};

    public static sharingScreen = false;
    public static sharingScreenSelf = false;
    public static screenShareStream = null;

    public static screenStream = null;

    public static expandedMode = false; //TODO: !!window.FORCED_CALL_EXPAND;

    public static mediaRecorders = {};
    public static audioChunksPerUser = {};

    public static joinDate = 0;

    public static leaveTime = 0;

    public static summaryRequest: Set<string> = new Set();

    private static mediaRecorderTranslation: MediaRecorder = null;
    private static audioContext;
    private static destination;
    public static lan = getLanguage();
    static recording: boolean;
    public static participantMediaRecorders: { [participantId: string]: MediaRecorder } = {};
    public static participantTimestamps: { [participantId: string]: number } = {};
    public static criticalSection: AsyncSemaphore = new AsyncSemaphore();

    public static init() {
        this.loadPreferences();

        AppEvents.AddEventListener('disconnect', this.onSocketDisconnected.bind(this));
        AppEvents.AddEventListener('room', this.onRoomChanged.bind(this));
        AppEvents.AddEventListener('room-leave', this.onRoomChanged.bind(this));

        AppEvents.AddEventListener('call-open', this.onCallOpen.bind(this));
        AppEvents.AddEventListener('call-close', this.onCallClose.bind(this));
        AppEvents.AddEventListener('breakout-assign', this.onBreakoutAssign.bind(this));
        AppEvents.AddEventListener('breakout-return', this.onBreakoutReturn.bind(this));

        AppEvents.AddEventListener('call-user-join', this.onCallUserJoin.bind(this));
        AppEvents.AddEventListener('call-users-snapshot', this.onCallUsersSnapshot.bind(this));
        AppEvents.AddEventListener('call-user-leave', this.onCallUserLeave.bind(this));

        AppEvents.AddEventListener('call-user-opt', this.onCallUserOptions.bind(this));

        AppEvents.AddEventListener('call-user-disc', this.onCallUserDisc.bind(this));

        AppEvents.AddEventListener('call-full-warning', this.onCallFullWarning.bind(this));

        AppEvents.AddEventListener('call-hand-turn', this.onCallHandTurn.bind(this));

        AppEvents.AddEventListener('call-mute', this.onCallMute.bind(this));

        AppEvents.AddEventListener('call-share-screen-start', this.onShareScreenStart.bind(this));
        AppEvents.AddEventListener('call-share-screen-end', this.onShareScreenEnd.bind(this));
        AppEvents.AddEventListener('call-share-screen-error', this.onShareScreenError.bind(this));

        AppEvents.AddEventListener('ask-recording', this.onAskRecording.bind(this));
        AppEvents.AddEventListener('stop-call-record', this.stopRecord.bind(this));
        AppEvents.AddEventListener('ask-stop-recording', this.onaskStopRecording.bind(this));
        AppEvents.AddEventListener('ask-summary', this.onAskSummary.bind(this));
        AppEvents.AddEventListener('call-date', this.onCallDate.bind(this));

        AppEvents.AddEventListener('translation', this.onTranslationReceived.bind(this));
        ExitPreventer.addPrevent("call", ['exit-page'], this.onExitRoom.bind(this));

        // if (this.expandedMode) {
        //     document.body.classList.add("call-expanded-mode");
        // } else {
        //     document.body.classList.remove("call-expanded-mode");
        // }
    }

    private static isCurrentCallEvent(event): boolean {
        return isCallEventForScope(event, {
            joinedCall: this.joinedCall,
            callOpen: this.callOpen,
            room: WebSocketController.room,
            callScopeId: this.callScopeId,
        });
    }

    private static onTranslationReceived(event) {
        if (!this.isCurrentCallEvent(event)) {
            return;
        }
        this.emit('translation-received', {
            callId: event.call_id,
            messages: Array.isArray(event.messages) ? event.messages : [],
        });
    }


    public static askRecording(rid: string, uid: string, code?: string) {
        WebSocketController.send({
            type: "ask-recording",
            room: rid,
            uid: uid,
            code: code,
        });
    }

    public static askStopRecording(rid: string, code?: string) {
        WebSocketController.send({
            type: "ask-stop-recording",
            room: rid,
            uid: this.selfId,
            code: code,
        });
    }

    public static onaskStopRecording(event) {
        this.emit("ask-stop-recording", event.user);
    }


    public static askSummary(rid: string, code?: string) {

        if (this.summaryRequest.has(rid)) {
            return;
        }
        this.summaryRequest.add(rid);

        WebSocketController.send({
            type: "ask-summary",
            room: rid,
            code: code
        });
    }

    public static onAskSummary() {
        this.emit("ask-summary");
    }

    public static clearSummary(rid: string) {
        this.summaryRequest.delete(rid);
    }

    public static onCallDate(event) {
        this.emit("call-date", event.date);
    }

    public static isSupported(): boolean {
        if (!UserMediaController.isUserMediaSupported()) {
            return false;
        }

        return true;
    }

    /* Events */

    public static addEventListener(event: string, listener: (...args: any[]) => void) {
        if (!CallsController.events[event]) {
            CallsController.events[event] = [];
        }
        CallsController.events[event].push(listener);
    }

    public static on(event: string, listener: (...args: any[]) => void) {
        CallsController.addEventListener(event, listener);
    }

    public static removeEventListener(event: string, listener: (...args: any[]) => void) {
        if (!CallsController.events[event]) {
            return;
        }
        const i = CallsController.events[event].indexOf(listener);
        if (i >= 0) {
            CallsController.events[event].splice(i, 1);
            if (CallsController.events[event].length === 0) {
                delete CallsController.events[event];
            }
        }
    }

    public static emit(event: string, ...args: any[]) {
        if (!CallsController.events[event]) {
            return;
        }
        for (const listener of [...CallsController.events[event]]) {
            try {
                listener(...args);
            } catch (e) {
                console.error("[CallsController] Error in listener for event '" + event + "':", e);
            }
        }
    }

    /* Utils */

    public static createRandomCode() {
        const chars = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
        let code = "";
        for (let i = 0; i < 9; i++) {
            code += chars[Math.floor(Math.random() * chars.length)];
        }
        return code;
    }

    public static findUserByPeer(pid: string) {
        for (const uid in this.callUsers) {
            if (this.callUsers[uid].peerId === pid) {
                return uid;
            }
        }
        return null;
    }

    public static findUserByScreenPeer(pid: string) {
        for (const uid in this.callUsers) {
            if (this.callUsers[uid].screenPeer === pid) {
                return uid;
            }
        }
        return null;
    }

    /**
     * Find the video RTCRtpSender on a peer connection.
     * First tries the direct sender.track.kind lookup; falls back to
     * transceiver-based search (handles Chrome when dummy track was stopped).
     */
    public static findVideoSender(pc: RTCPeerConnection): RTCRtpSender | null {
        const direct = pc.getSenders().find(s => s.track?.kind === "video");
        if (direct) return direct;
        // Fallback: find the transceiver whose receiver negotiated video
        try {
            const t = pc.getTransceivers().find(t => t.receiver?.track?.kind === "video");
            if (t?.sender) return t.sender;
        } catch (_) { /**/ }
        return null;
    }

    /**
     * Replace a track on an RTCRtpSender with a single retry on failure.
     */
    public static replaceTrackWithRetry(sender: RTCRtpSender, track: MediaStreamTrack | null, uid: string) {
        sender.replaceTrack(track).catch(err => {
            console.warn("replaceTrack failed, retrying in 500ms:", err);
            setTimeout(() => {
                if (!this.joinedCall || !this.callUsers[uid]) return;
                sender.replaceTrack(track).catch(err2 => {
                    console.error("replaceTrack retry failed:", err2);
                });
            }, 500);
        });
    }

    /* Preferences */

    public static loadPreferences() {
        try {
            this.preferences = fetchFromLocalStorageCache("call-preferences", { audio: true, video: true, });
        } catch (ex) {
            console.error(ex);
            this.preferences = {
                audio: true,
                video: true,
            };
        }

        this.audio = this.preferences.audio !== false;
        this.video = false;
    }

    public static savePreferences() {
        try {
            saveIntoLocalStorage("call-preferences", this.preferences);
        } catch (ex) {
            console.error(ex);
        }
    }

    public static setPreferredAudioInputDevice(deviceId: string) {
        if (!this.preferences) {
            this.preferences = { audio: true, video: true };
        }
        (this.preferences as any).audioDeviceId = (deviceId || "").trim();
        this.savePreferences();
    }

    public static getPreferredAudioInputDevice(): string {
        return (((this.preferences as any)?.audioDeviceId || "") + "").trim();
    }

    public static setPreferredVideoInputDevice(deviceId: string) {
        if (!this.preferences) {
            this.preferences = { audio: true, video: true };
        }
        (this.preferences as any).videoDeviceId = (deviceId || "").trim();
        this.savePreferences();
    }

    public static getPreferredVideoInputDevice(): string {
        return (((this.preferences as any)?.videoDeviceId || "") + "").trim();
    }

    private static buildAudioInputConstraints(deviceId?: string): MediaTrackConstraints {
        const c: MediaTrackConstraints = {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            channelCount: { ideal: 1 },
            sampleRate: { ideal: 48000 },
        };
        const id = (deviceId || "").trim();
        if (id) {
            c.deviceId = { exact: id };
        }
        return c;
    }

    private static buildVideoInputConstraints(deviceId?: string): MediaTrackConstraints {
        const c: MediaTrackConstraints = {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 24, max: 30 },
        };
        const id = (deviceId || "").trim();
        if (id) {
            c.deviceId = { exact: id };
        }
        return c;
    }

    public static findAudioSender(pc: RTCPeerConnection): RTCRtpSender | null {
        const direct = pc.getSenders().find(s => s.track?.kind === "audio");
        if (direct) return direct;
        try {
            const t = pc.getTransceivers().find(t => t.receiver?.track?.kind === "audio");
            if (t?.sender) return t.sender;
        } catch (_) { /**/ }
        return null;
    }

    public static async switchAudioInputDevice(deviceId: string): Promise<boolean> {
        const targetDevice = (deviceId || "").trim();
        this.setPreferredAudioInputDevice(targetDevice);

        if (!this.joinedCall || !this.selfUserStream) {
            return true;
        }

        const stream = await navigator.mediaDevices.getUserMedia({
            video: false,
            audio: this.buildAudioInputConstraints(targetDevice),
        });

        const newTrack = stream.getAudioTracks()[0];
        if (!newTrack) {
            UserMediaController.stopStream(stream);
            return false;
        }

        // Keep current mute state when swapping input device
        newTrack.enabled = this.audio && !this.modMuted;

        for (const uid of Object.keys(this.callUsers)) {
            const pc = (this.callUsers[uid].call as any)?.peerConnection as RTCPeerConnection | undefined;
            if (!pc) continue;
            const audioSender = this.findAudioSender(pc);
            if (audioSender) {
                this.replaceTrackWithRetry(audioSender, newTrack, uid);
            }
        }

        const prevAudioTracks = this.selfUserStream.getAudioTracks();
        prevAudioTracks.forEach(track => {
            this.selfUserStream.removeTrack(track);
        });
        this.selfUserStream.addTrack(newTrack);

        if (this.selfUserStreamAudio) {
            this.selfUserStreamAudio.getTracks().forEach(track => {
                track.stop();
                this.selfUserStreamAudio.removeTrack(track);
            });
        }
        this.selfUserStreamAudio = new MediaStream([newTrack]);

        prevAudioTracks.forEach(track => {
            try { track.stop(); } catch (_) { /**/ }
        });

        return true;
    }

    public static async switchVideoInputDevice(deviceId: string): Promise<boolean> {
        const targetDevice = (deviceId || "").trim();
        this.setPreferredVideoInputDevice(targetDevice);

        // If camera is currently off, only persist preference.
        if (!this.joinedCall || !this.video || !this.selfUserStream) {
            return true;
        }

        const stream = await navigator.mediaDevices.getUserMedia({
            video: this.buildVideoInputConstraints(targetDevice),
            audio: false,
        });

        const newTrack = stream.getVideoTracks()[0];
        if (!newTrack) {
            UserMediaController.stopStream(stream);
            return false;
        }

        for (const uid of Object.keys(this.callUsers)) {
            const pc = (this.callUsers[uid].call as any)?.peerConnection as RTCPeerConnection | undefined;
            if (!pc) continue;
            const videoSender = this.findVideoSender(pc);
            if (videoSender) {
                this.replaceTrackWithRetry(videoSender, newTrack, uid);
            }
        }

        const prevVideoTracks = this.selfUserStream.getVideoTracks().filter(t => t !== this.dummyVideoTrack);
        prevVideoTracks.forEach(track => {
            this.selfUserStream.removeTrack(track);
        });
        if (this.dummyVideoTrack && this.selfUserStream.getVideoTracks().includes(this.dummyVideoTrack)) {
            this.selfUserStream.removeTrack(this.dummyVideoTrack);
        }
        this.selfUserStream.addTrack(newTrack);

        prevVideoTracks.forEach(track => {
            try { track.stop(); } catch (_) { /**/ }
        });

        this.emit("self-change", this.video, this.audio, this.hand);
        return true;
    }

    public static enableAudio(enabled: boolean) {
        this.audio = enabled;
        this.preferences.audio = enabled;

        this.savePreferences();

        if (this.joinedCall) {
            if (!this.modMuted) {
                UserMediaController.setMediaStreamAudioEnabled(this.selfUserStream, enabled);
            }
            WebSocketController.send({
                "type": "call-opt",
                "video": this.video,
                "audio": this.audio,
                "hand": this.hand,
            });
        }

        this.emit("self-change", this.video, this.audio, false);
    }

    public static enableVideo(enabled: boolean) {
        if (this.joinedCall) {
            if (!enabled && this.video) {
                this.video = false;
                this.preferences.video = false;
                this.savePreferences();

                // Replace the real camera track with the silent dummy on every peer connection.
                // Using replaceTrack(dummyTrack) instead of replaceTrack(null) keeps the sender's
                // track non-null so we can find it again by kind="video" when re-enabling.
                for (const uid of Object.keys(this.callUsers)) {
                    const pc = (this.callUsers[uid].call as any)?.peerConnection as RTCPeerConnection | undefined;
                    if (!pc) continue;
                    const videoSender = this.findVideoSender(pc);
                    if (videoSender) {
                        const target = this.dummyVideoTrack || null;
                        this.replaceTrackWithRetry(videoSender, target, uid);
                    }
                }

                // Stop the real camera track(s) and restore the dummy in selfUserStream.
                this.selfUserStream.getVideoTracks().forEach(track => {
                    if (track !== this.dummyVideoTrack) {
                        track.stop();
                        this.selfUserStream.removeTrack(track);
                    }
                });
                // Ensure dummy is back in selfUserStream (may have been removed on enable).
                if (this.dummyVideoTrack && !this.selfUserStream.getVideoTracks().includes(this.dummyVideoTrack)) {
                    this.selfUserStream.addTrack(this.dummyVideoTrack);
                }

                WebSocketController.send({
                    "type": "call-opt",
                    "video": this.video,
                    "audio": this.audio,
                    "hand": this.hand,
                });

                this.emit("self-change", this.video, this.audio, this.hand);
            } else if (enabled && !this.video) {
                // Optimistic UI update — will be reverted if camera access fails
                this.video = true;
                this.preferences.video = true;
                this.savePreferences();
                this.emit("self-change", this.video, this.audio, this.hand);

                const preferredVideoDeviceId = this.getPreferredVideoInputDevice();
                navigator.mediaDevices.getUserMedia({
                    video: this.buildVideoInputConstraints(preferredVideoDeviceId),
                    audio: false,
                })
                    .then((stream) => {
                        if (!this.joinedCall || !this.video) {
                            if (stream) UserMediaController.stopStream(stream);
                            return;
                        }

                        if (!stream) {
                            this.video = false;
                            this.preferences.video = false;
                            this.savePreferences();
                            this.emit("self-change", this.video, this.audio, this.hand);
                            this.emit("error-msg", "Error", "The camera could not be accessed.");
                            return;
                        }

                        if (this.dummyVideoTrack) {
                            this.selfUserStream.removeTrack(this.dummyVideoTrack);
                        }
                        UserMediaController.injectVideo(this.selfUserStream, stream);

                        const videoTrack = this.selfUserStream.getVideoTracks()[0];

                        WebSocketController.send({
                            "type": "call-opt",
                            "video": this.video,
                            "audio": this.audio,
                            "hand": this.hand,
                        });

                        if (videoTrack) {
                            for (const uid of Object.keys(this.callUsers)) {
                                const pc = (this.callUsers[uid].call as any)?.peerConnection as RTCPeerConnection | undefined;
                                if (!pc) continue;
                                const videoSender = this.findVideoSender(pc);
                                if (videoSender) {
                                    this.replaceTrackWithRetry(videoSender, videoTrack, uid);
                                } else {
                                    try { pc.addTrack(videoTrack, this.selfUserStream); } catch (err) { console.error("addTrack fallback:", err); }
                                }
                            }
                        }
                    })
                    .catch((err) => {
                        this.video = false;
                        this.preferences.video = false;
                        this.savePreferences();
                        this.emit("self-change", this.video, this.audio, this.hand);
                        this.emit("error-msg", "Error", "The camera could not be accessed.");
                        console.error("Error accessing preferred camera:", err);
                    });
            }
        } else {
            this.video = enabled;
            this.preferences.video = enabled;
            this.savePreferences();
            this.emit("self-change", this.video, this.audio, this.hand);
        }
    }

    public static raiseHand(raised) {
        this.hand = raised;
        if (this.joinedCall) {
            WebSocketController.send({
                "type": "call-opt",
                "video": this.video,
                "audio": this.audio,
                "hand": this.hand,
            });
        }

        this.emit("self-change", this.video, this.audio, this.hand);
    }

    /* Events */

    public static onAskRecording(event) {
        this.recordID = event.record;
        this.emit("ask-recording");
    }


    public static onExitRoom(cont) {
        if (!cont) {
            return this.joinedCall;
        }
        if (this.joinedCall) {
            AppEvents.Emit("ask-leave-room", cont);
        } else {
            cont();
        }
    }
    /* Websocket event handlers */

    public static onSocketDisconnected() {
        // A forced transition deliberately destroys the previous PeerJS object.
        // That close event must not be treated as a failed breakout join.  Only
        // a disconnect from the currently active call can trigger the one-shot
        // fallback to the main call.
        const shouldFallbackToMain = this.breakoutSwitching && !this.breakoutFallbackUsed && this.joinedCall;
        if (this.joinedCall) {
            if (!shouldFallbackToMain) {
                this.emit("error-msg", $t("Error"), $t("You left the call due to a connection error."));
            }
            this.emit("close", {
                preserveRecording: shouldFallbackToMain,
                preserveBreakoutPanel: shouldFallbackToMain,
                preserveSubtitles: true,
            });
            if (shouldFallbackToMain) {
                this.emit("error-msg", $t("Error"), $t("The breakout group could not be joined; returning to the main call."));
            }
        }

        if (shouldFallbackToMain) {
            this.breakoutFallbackUsed = true;
            this.breakoutSwitching = true;
            this.breakoutAttemptCode = "";
            this.breakoutDesiredCode = "";
            this.closed = true;
            this.destroyCallData();
            this.closed = false;
            this.joinCall("");
            return;
        }

        this.destroyCallData();
        this.rejoinCall();
    }

    public static onRoomChanged(msg) {
        if (msg && msg.room === this.room) {
            return; // Same room
        }
        // If in a call, keep the call alive regardless of room navigation
        if (this.joinedCall) {
            return;
        }
        this.room = msg ? msg.room : "";
        this.destroyCallData();
    }

    public static onCallOpen(event) {
        if (!this.joinedCall || !event || event.room !== WebSocketController.room
            || typeof event.call_id !== "string") return;
        this.selfId = event.user_id;
        this.selfName = event.user_name;
        this.selfImage = event.user_image;
        this.hand = false;
        this.callId = event.call_id;
        this.callScopeId = normalizeCallScopeId(event.call_id);
        this.callOpen = true;
        this.modMuted = !!event.muted;
        // The call-open event carries the room-level recording state.  Keep the
        // controller state in sync when a breakout transition replaces the
        // current PeerJS call, otherwise the views briefly show recording as
        // stopped even though the room is still being recorded.
        if (typeof event.status === "boolean") {
            this.recording = event.status;
            RoomController.recording = event.status;
            if (!event.status) {
                this.adminRecord = "";
                RoomController.recordingUser = "";
            }
        }
        if (this.modMuted) {
            UserMediaController.setMediaStreamAudioEnabled(this.selfUserStream, false);
        }
        this.callCode = event.call_code || "";
        this.breakoutCall = !!event.breakout;
        this.emit("breakout-status-change", this.breakoutCall);
        this.emit("open", event.user_id, this.audioMode ? this.selfUserStreamAudio : this.selfUserStream, { name: event.user_name, image: event.user_image }, event.muted, this.audioMode, event);
        this.emit("join-code-change", event.call_code || "");
        this.resumeRecordingForCurrentCall();
        // Emit self-change immediately so any listener (e.g. GlobalCallWidget) can
        // sync its local audio/video/hand state from the controller's actual values
        // rather than relying on its own hardcoded initial data.
        this.emit("self-change", this.video, this.modMuted ? false : this.audio, this.hand);

        // Start Azure only after the server has opened this call and only when
        // the user has enabled subtitles. This avoids spending Azure resources
        // while the captions UI is disabled or while a replacement is pending.
        if (RoomController.subtitlesActive) {
            this.startTranslation();
        }

        if (this.breakoutSwitching && event.call_code === this.breakoutAttemptCode) {
            const desiredCode = this.breakoutDesiredCode;
            if (desiredCode !== this.breakoutAttemptCode) {
                // A newer assignment/return arrived while joining.  Finish
                // this server acknowledgement, then immediately join the
                // latest destination.
                this.breakoutAttemptCode = desiredCode || "";
                this.joinCall(this.breakoutAttemptCode);
                return;
            }

            const restoreVideo = this.breakoutRestoreVideo;
            this.breakoutSwitching = false;
            this.breakoutAttemptCode = "";
            this.breakoutDesiredCode = null;
            this.breakoutFallbackUsed = false;

            // A regular join starts with video disabled.  Restore the state
            // from before the forced move once the new call is open.
            if (restoreVideo) {
                setTimeout(() => {
                    if (this.joinedCall) {
                        this.enableVideo(true);
                    }
                }, 0);
            }
        }
    }

    private static onBreakoutAssign(event) {
        // The websocket server sends only the assignment addressed to this
        // connection. It can therefore be used both for students in the main
        // call and for an administrator moving between private calls.
        if (!event || event.room !== WebSocketController.room || !event.call_code
            || (!this.joinedCall && !this.breakoutSwitching)) {
            return;
        }
        const destination = (event.call_code + "").substr(0, 80);
        if (this.breakoutSwitching) {
            this.breakoutDesiredCode = destination;
            return;
        }
        if (!this.breakoutSwitching && this.callCode === destination) {
            return;
        }
        this.moveToBreakoutCall(destination);
    }

    private static onBreakoutReturn(event) {
        if (!event || event.room !== WebSocketController.room) {
            return;
        }

        const returnedCodes: string[] | null = Array.isArray(event.call_codes)
            ? event.call_codes.map(code => (code + "").substr(0, 80))
            : event.call_code ? [(event.call_code + "").substr(0, 80)] : null;

        // Return-all carries the exact breakout codes, so a regular private
        // tutoring call in the same room is not treated as a workroom.
        // During a transition, compare against the pending destination too.
        if (returnedCodes && returnedCodes.length === 0) {
            return;
        }
        if (returnedCodes && !returnedCodes.includes(this.callCode)
            && !returnedCodes.includes(this.breakoutAttemptCode)
            && !returnedCodes.includes(this.breakoutDesiredCode || "")) {
            return;
        }
        if (!returnedCodes && !this.breakoutSwitching && !this.callCode) {
            return;
        }

        if (this.breakoutSwitching) {
            this.breakoutDesiredCode = "";
            return;
        }
        if (!this.joinedCall || !this.callCode) {
            return;
        }
        this.moveToBreakoutCall("");
    }

    private static moveToBreakoutCall(code: string) {
        if (!this.joinedCall || !WebSocketController.room) {
            return;
        }

        this.breakoutDesiredCode = code;
        if (this.breakoutSwitching) {
            return;
        }

        this.breakoutSwitching = true;
        this.breakoutAttemptCode = code;
        this.breakoutRestoreVideo = this.video;
        this.breakoutFallbackUsed = false;
        this.joinCall(code);
    }

    /**
     * Recover once if the forced destination cannot even be opened (for
     * example, a PeerJS error before the socket is open or a failed call-info
     * request).  The fallback itself is a normal main-call join and therefore
     * is not retried as another breakout transition.
     */
    private static fallbackFromBreakoutFailure(): boolean {
        if (!this.breakoutSwitching || this.breakoutFallbackUsed) {
            return false;
        }

        this.breakoutFallbackUsed = true;
        this.breakoutSwitching = true;
        this.breakoutAttemptCode = "";
        this.breakoutDesiredCode = "";
        this.emit("error-msg", ("Error"), ("The breakout group could not be joined; returning to the main call."));
        this.closed = true;
        this.destroyCallData();
        this.closed = false;
        this.joinCall("");
        return true;
    }

    /** Cancel a pending forced move when the user explicitly leaves. */
    public static cancelBreakoutSwitch() {
        this.breakoutSwitching = false;
        this.breakoutAttemptCode = "";
        this.breakoutDesiredCode = null;
        this.breakoutFallbackUsed = false;
    }

    public static onCallClose(event) {
        if (!event || typeof event.reason !== "string"
            || typeof event.call_id !== "string") return;
        if (event.room !== WebSocketController.room) return;

        // A replacement's LEFT may arrive after call-open. Consume that exact
        // acknowledgement even for main -> main transitions (wire id "").
        if (event.reason === "LEFT") {
            const pendingLeaveIndex = this.pendingReplacementCallIds.indexOf(event.call_id);
            if (pendingLeaveIndex !== -1) {
                this.pendingReplacementCallIds.splice(pendingLeaveIndex, 1);
                return;
            }
        }

        if (!this.joinedCall) return;

        const eventScopeId = normalizeCallScopeId(event.call_id);
        if (this.callOpen && eventScopeId !== null && eventScopeId !== this.callScopeId) {
            return;
        }
        // A leave acknowledgement from the old call may arrive while PeerJS
        // is already connected to the replacement, but before its call-open.
        if (event?.reason === "LEFT" && !this.callOpen) {
            return;
        }
        // The old call sends LEFT while a forced transition is replacing it.
        // Ignore that acknowledgement if it races with the new call-open.
        if (this.breakoutSwitching && event.reason === "LEFT") return;
        const forcedTransition = this.breakoutSwitching && !this.breakoutFallbackUsed;
        const preserveRecording = this.breakoutSwitching;
        this.hand = false;
        this.callId = "";
        this.callScopeId = "";
        this.callOpen = false;
        this.callCode = "";
        let shouldRejoin = false;
        switch (event.reason) {
            case "BANNED":
                this.emit("error-msg", ("Error"), ("You are banned and cannot join the call."));
                break;
            case "ALREADY_IN_CALL":
                shouldRejoin = !forcedTransition;
                break;
            case "FULL":
                if (!forcedTransition) {
                    this.emit("error-msg", ("Error"), ("The call is full and it does not accept more users in it."));
                }
                break;
            case "TIME_LIMIT":
                if (!forcedTransition) {
                    this.emit("error-msg", ("Error"), ("This room reached the time limit for the month. On the first day next month, the time usage will be reset."));
                }
                break;
            case "PRIVATE_CALLS_LIMIT":
                if (!forcedTransition) {
                    this.emit("error-msg", ("Error"), ("This room reached the limit on the amount of private calls it can have."));
                }
                break;
            case "CALL_CLOSED":
                if (!forcedTransition) {
                    this.emit("error-msg", ("Call closed"), ("The call was closed."));
                }
                break;
            case "KICK":
                if (!forcedTransition) {
                    this.emit("error-msg", ("Call closed"), ("You were kicked from the call."));
                }
                break;
        }
        if (!preserveRecording && this.recording && this.adminRecord == this.selfId) {
            this.stopRecord(this.recordID, this.selfId);
        }
        this.emit("close", {
            preserveRecording,
            preserveBreakoutPanel: this.breakoutSwitching,
            preserveSubtitles: this.breakoutSwitching,
        });
        this.stopTranslation();
        if (!this.breakoutSwitching) {
            this.emit("join-code-change", "");
        }
        if (shouldRejoin) {
            this.destroyCallData();
            this.rejoinCall();
        } else {
            const shouldFallbackToMain = this.breakoutSwitching && !this.breakoutFallbackUsed;
            if (shouldFallbackToMain) {
                this.breakoutFallbackUsed = true;
                // Keep the transition state active for the fallback join so
                // the pre-move camera preference is restored on call-open.
                this.breakoutSwitching = true;
                this.breakoutAttemptCode = "";
                this.breakoutDesiredCode = "";
                this.emit("error-msg", ("Error"), ("The breakout group could not be joined; returning to the main call."));
            }
            this.closed = true;
            this.destroyCallData();
            if (shouldFallbackToMain) {
                this.closed = false;
                this.joinCall("");
                return;
            }
            // Sync WebSocket to the room the user navigated to while in the call
            const targetRoom = RoomController.pendingWebSocketRoom || RoomController.room;
            RoomController.pendingWebSocketRoom = "";
            if (targetRoom) {
                WebSocketController.joinRoom(targetRoom);
            } else {
                WebSocketController.leaveRoom();
            }
        }
    }

    public static onCallUserJoin(event) {
        if (!this.isCurrentCallEvent(event) || typeof event.peer_id !== "string" || !event.peer_id.trim()) return;
        const uid = event.user_id;
        const peerId = event.peer_id.trim();
        if (this.selfId === uid) return;

        // If a stale entry exists (e.g. fast disconnect → reconnect), clean it up first
        // so the new peer connection and UI state are properly re-established.
        if (this.callUsers[uid]) {
            const stale = this.callUsers[uid];
            if (stale.peerId === peerId) {
                // The same participant may arrive through the initial roster
                // and the live join event. This is not a reconnect.
                return;
            }
            if (stale.peerId && this.pendingCalls[stale.peerId]) {
                delete this.pendingCalls[stale.peerId];
            }
            if (stale.call) {
                try {
                    stale.call.removeAllListeners();
                    stale.call.close();
                } catch (ex) {
                    console.error(ex);
                }
            }
            delete this.callUsers[uid];
            // Notify UI the stale entry is gone before adding the fresh one
            this.emit("user-leave", uid);
        }

        this.callUsers[uid] = {
            id: uid,
            stream: null,
            call: null,
            peerId,
            muted: !!event.muted,
            audio: !!event.audio,
            video: !!event.video,
            hand: !!event.hand,
            audio_mode: !!event.audio_mode,
            userInfo: {
                name: event.user_name,
                image: event.user_image,
            },
            flags: event.flags,
            serializations: event.serializations,
        };

        // Event
        this.emit("user-join", uid, this.callUsers[uid].userInfo, this.callUsers[uid].muted, this.callUsers[uid].video, this.callUsers[uid].audio, this.callUsers[uid].flags, this.callUsers[uid].hand, this.callUsers[uid].audio_mode);

        // Call
        if (this.peerID < this.callUsers[uid].peerId) {
            // We must call the other peer, the other peer must answer
            this.callPeer(uid, this.callUsers[uid].peerId);
        } else if (this.pendingCalls[this.callUsers[uid].peerId]) {
            this.onCall(this.pendingCalls[this.callUsers[uid].peerId]);
            this.pendingCalls[this.callUsers[uid].peerId] = null;
        }

        // Screen share
        if (this.screenPeer && this.screenStream) {
            this.callPeerScreen(uid, this.callUsers[uid].peerId);
        }
    }

    public static onCallUsersSnapshot(event) {
        if (!this.isCurrentCallEvent(event) || !Array.isArray(event.users)) {
            return;
        }

        for (const participant of event.users) {
            if (!shouldHydrateSnapshotParticipant(participant, this.selfId, this.callUsers)) {
                continue;
            }
            this.onCallUserJoin({
                ...participant,
                event: "call-user-join",
                room: event.room,
                call_id: event.call_id,
            });
        }
    }

    public static onCallUserLeave(event) {
        if (!this.isCurrentCallEvent(event)) return;

        const uid = event.user_id;

        if (!this.callUsers[uid]) return;

        const user = this.callUsers[uid];

        // Remove the pending call entry so future connections don't accidentally claim it
        if (user.peerId && this.pendingCalls[user.peerId]) {
            delete this.pendingCalls[user.peerId];
        }

        if (user.call) {
            try {
                user.call.removeAllListeners();
                user.call.close();
            } catch (ex) {
                // Swallow — cleanup must always complete even if PeerJS throws
                console.error(ex);
            }
        }

        delete this.callUsers[uid];
        this.emit("user-leave", uid);
    }

    public static onCallMute(event) {
        if (!this.isCurrentCallEvent(event)) return;
        if (event.user_id !== this.selfId && this.callUsers[event.user_id]) {
            this.callUsers[event.user_id].muted = event.muted;
            if (this.callUsers[event.user_id].audio) {
                if (event.muted) {
                    UserMediaController.setMediaStreamAudioEnabled(this.callUsers[event.user_id].stream, false);
                } else {
                    UserMediaController.setMediaStreamAudioEnabled(this.callUsers[event.user_id].stream, true);
                }
            }
            this.emit("user-change", event.user_id, this.callUsers[event.user_id].muted, this.callUsers[event.user_id].video, this.callUsers[event.user_id].audio, this.callUsers[event.user_id].hand);
        } else if (event.user_id === this.selfId) {
            this.modMuted = event.muted;
            if (this.audio) {
                if (event.muted) {
                    UserMediaController.setMediaStreamAudioEnabled(this.selfUserStreamAudio, false);
                } else {
                    UserMediaController.setMediaStreamAudioEnabled(this.selfUserStreamAudio, true);
                }
            }
            this.emit("self-mute", event.muted);
        }
    }

    public static onShareScreenStart(event) {
        if (!this.isCurrentCallEvent(event)) return;
        const uid = event.user_id;

        if (!this.screenShareData[uid]) {
            this.screenShareData[uid] = {
                uid: event.user_id,
                name: event.user_name,
                image: event.user_image,
                peerId: "",
                stream: null,
                call: null,
            };
        }

        this.screenShareData[uid].uid = event.user_id;
        this.screenShareData[uid].name = event.user_name;
        this.screenShareData[uid].image = event.user_image;
        this.screenShareData[uid].peerId = event.peer_id;

        if (this.selfId === uid) {
            this.emit('screen-share-start', true, uid, {
                name: event.user_name,
                image: event.user_image,
            });

            this.screenShareData[uid].stream = this.screenStream;

            UserMediaController.detectStreamEnding(this.screenStream, this.endShareScreen.bind(this));

            this.emit('screen-share-stream', uid, this.screenShareData[uid].stream);

            // Call every other peer
            for (const uid in this.callUsers) {
                this.callPeerScreen(uid, this.callUsers[uid].peerId);
            }
        } else {
            const ssPeer = event.peer_id;
            this.emit('screen-share-start', false, uid, {
                name: event.user_name,
                image: event.user_image,
            });

            if (this.pendingScreenCalls[ssPeer]) {
                this.onScreenShareCall(this.pendingScreenCalls[ssPeer]);
                this.pendingScreenCalls[ssPeer] = null;
            }
        }
    }

    public static onShareScreenEnd(event) {
        if (!this.isCurrentCallEvent(event)) return;

        const uid = event.user_id;

        if (this.screenShareData[uid]) {
            if (this.screenShareData[uid].call) {
                this.screenShareData[uid].call.removeAllListeners();
                this.screenShareData[uid].call.close();
                this.screenShareData[uid].call = null;
            }

            delete this.screenShareData[uid];
            this.emit('screen-share-end', uid);
        }

        if (this.selfId === uid) {
            // The screen share was forced to end
            if (this.screenPeer) {
                this.screenPeer.destroy();
                this.screenPeer = null;
            }

            this.screenPeerId = "";

            if (this.screenStream) {
                UserMediaController.stopStream(this.screenStream);
                this.screenStream = null;
            }
        }
    }

    public static onCallUserOptions(event) {
        if (!this.isCurrentCallEvent(event)) return;
        if (event.user_id !== this.selfId && this.callUsers[event.user_id]) {
            this.callUsers[event.user_id].video = event.video;
            this.callUsers[event.user_id].audio = event.audio;
            if (this.callUsers[event.user_id].hand !== event.hand) {
                this.emit("raised-hand", { name: event.name, uid: event.user_id });
            }
            this.callUsers[event.user_id].hand = event.hand;
            this.emit("user-change", event.user_id, this.callUsers[event.user_id].muted, this.callUsers[event.user_id].video, this.callUsers[event.user_id].audio, this.callUsers[event.user_id].hand);
        }
    }

    public static onCallUserDisc(event) {
        if (!this.isCurrentCallEvent(event)) return;
        const pid = event.peer_id + "";
        const uid = event.user_id + "";

        if (pid === this.peerID) {
            if (this.callUsers[uid]) {
                if (this.callUsers[uid].call) {
                    try {
                        this.callUsers[uid].call.removeAllListeners();
                        this.callUsers[uid].call.close();
                    } catch (ex) {
                        console.error(ex);
                    }
                    this.callUsers[uid].call = null;
                }
                // Clear stale stream so the player reflects the disconnected state
                // immediately (server will follow up with call-user-leave).
                if (this.callUsers[uid].stream) {
                    this.callUsers[uid].stream = null;
                    this.emit("user-stream", uid, null);
                }
                this.emit("user-change", uid,
                    this.callUsers[uid].muted,
                    false, // video unavailable while disconnected
                    false, // audio unavailable while disconnected
                    this.callUsers[uid].hand,
                );
            }
        } else if (pid === this.screenPeerId) {
            if (this.callUsers[uid] && this.callUsers[uid].screenCall) {
                try {
                    this.callUsers[uid].screenCall.removeAllListeners();
                    this.callUsers[uid].screenCall.close();
                } catch (ex) {
                    console.error(ex);
                }
                this.callUsers[uid].screenCall = null;
            }
        }
    }

    public static onCallFullWarning(event) {
        if (!this.isCurrentCallEvent(event)) return;
        this.emit("call-full-warning", event.user_name || "");
    }

    public static onCallHandTurn(event) {
        if (!this.isCurrentCallEvent(event)) return;
        if (event.user_id !== this.selfId && this.callUsers[event.user_id]) {
            this.callUsers[event.user_id].hand = false;
            this.emit("user-change", event.user_id, this.callUsers[event.user_id].muted, this.callUsers[event.user_id].video, this.callUsers[event.user_id].audio, this.callUsers[event.user_id].hand);
        } else {
            this.hand = false;
            this.emit("self-change", this.video, this.audio, this.hand);
        }

        this.emit("hand-turn", event.user_id);
    }

    public static onShareScreenError(event) {
        if (!this.isCurrentCallEvent(event)) return;
        switch (event.reason) {
            case "ALREADY":
                this.emit("error-msg", ("Error"), ("There is already another user sharing screen."));
                break;
            default:
                this.emit("error-msg", ("Error"), ("Cannot share screen in this room."));
        }

        if (this.screenPeer) {
            this.screenPeer.destroy();
            this.screenPeer = null;
        }

        this.screenPeerId = "";

        if (this.screenStream) {
            UserMediaController.stopStream(this.screenStream);
            this.screenStream = null;
        }
    }

    public static onCall(call) {
        if (call.metadata === "screen") {
            return this.onScreenShareCall(call);
        }
        const pid = call.peer;
        if (this.peerID <= pid) return; // My pid must be greater for me to accept
        const uid = this.findUserByPeer(pid);
        if (!uid) {
            this.pendingCalls[pid] = call;
            return; // Mark as pending for now
        }

        // Accept call

        if (this.callUsers[uid].call) {
            try {
                this.callUsers[uid].call.removeAllListeners();
                this.callUsers[uid].call.close();
            } catch (ex) {
                console.error(ex);
            }
        }

        call.answer((this.audioMode || this.callUsers[uid].audio_mode) ? this.selfUserStreamAudio : this.selfUserStream);

        this.callUsers[uid].call = call;
        call.on("stream", (stream) => {
            // Guard: ignore if this call was superseded by a newer one
            if (!this.callUsers[uid] || this.callUsers[uid].call !== call) return;
            this.callUsers[uid].stream = stream;
            this.resumeRecordingForParticipant(uid, stream);
            this.emit("user-stream", uid, stream);
        });

        let callClosed = false;

        this.callUsers[uid].call.on("error", (err) => {
            console.error(err);

            if (callClosed) {
                return;
            }
            callClosed = true;

            WebSocketController.send({
                type: "call-disc",
                peer: pid,
            });
        });

        this.callUsers[uid].call.on("close", () => {
            if (callClosed) {
                return;
            }
            callClosed = true;

            WebSocketController.send({
                type: "call-disc",
                peer: pid,
            });
        });
    }

    public static findScreenShareUID(peerID) {
        for (const u in this.screenShareData) {
            if (this.screenShareData[u].peerId === peerID) {
                return u;
            }
        }
        return null;
    }

    public static onScreenShareCall(call) {
        const peerId = call.peer;
        // console.log("[SCREEN SHARE] Received call from peer: " + call.peer);
        const uid = this.findScreenShareUID(call.peer);
        // console.log("[SCREEN SHARE] Call from USER:  " + uid);
        if (!uid) {
            this.pendingScreenCalls[call.peer] = call;
            return;
        }

        // Accept call
        call.answer(null); // Unidirectional

        if (this.screenShareData[uid].call) {
            try {
                this.screenShareData[uid].call.removeAllListeners();
                this.screenShareData[uid].call.close();
            } catch (ex) {
                console.error(ex);
            }
        }

        let callClosed = false;

        this.screenShareData[uid].call = call;
        this.screenShareData[uid].call.on("stream", (stream) => {
            this.screenShareData[uid].stream = stream;
            this.emit("screen-share-stream", uid, stream);
        });
        this.screenShareData[uid].call.on("error", (err) => {
            console.error(err);

            if (callClosed) {
                return;
            }
            callClosed = true;

            WebSocketController.send({
                type: "call-disc",
                peer: peerId,
            });
        });

        this.screenShareData[uid].call.on("close", () => {
            if (callClosed) {
                return;
            }
            callClosed = true;

            WebSocketController.send({
                type: "call-disc",
                peer: peerId,
            });
        });
    }

    /* Actions */

    private static pauseRecordingForBreakoutTransition() {
        for (const participantId of Object.keys(this.mediaRecorders)) {
            const recorder = this.mediaRecorders[participantId];
            if (!recorder || recorder.state !== "recording") {
                continue;
            }
            try {
                // Flush the current segment, but do not finish the server-side
                // record. The next call creates a new recorder for the same ID.
                recorder.stop();
            } catch (ex) {
                console.error("Error pausing call recording for breakout transition:", ex);
            }
        }
        this.mediaRecorders = {};
    }

    public static destroyCallData() {
        // Invalidate the websocket scope before changing joinedCall. Events
        // already in flight must not match the next call, especially the main
        // call whose wire id is an empty string.
        this.callOpen = false;
        this.callId = "";
        this.callScopeId = "";
        this.callCode = "";
        this.joinedCall = false;

        if (this.breakoutCall) {
            this.breakoutCall = false;
            this.emit("breakout-status-change", false);
        }

        if (this.breakoutSwitching) {
            this.pauseRecordingForBreakoutTransition();
        }

        // Stop any active translation session so the backend cleans up
        // the Azure Speech recognizer and avoids memory leaks.
        this.stopTranslation();

        if (this.rejoinTimeout) {
            clearTimeout(this.rejoinTimeout);
            this.rejoinTimeout = null;
        }

        // Close calls

        for (const u in this.callUsers) {
            if (this.callUsers[u].call) {
                try {
                    this.callUsers[u].call.removeAllListeners();
                    this.callUsers[u].call.close();
                } catch (ex) {
                    console.error(ex);
                }
            }
        }

        for (const u in this.screenShareData) {
            if (this.screenShareData[u].call) {
                try {
                    this.screenShareData[u].call.removeAllListeners();
                    this.screenShareData[u].call.close();
                    this.screenShareData[u].call = null;
                } catch (e) {
                    console.error(e);
                }
            }
        }

        // Destroy peers

        if (this.peer) {
            this.peer.destroy();
            this.peer = null;
        }

        if (this.screenPeer) {
            this.screenPeer.destroy();
            this.screenPeer = null;
        }

        this.screenPeerId = "";

        this.screenShareData = {};
        this.callUsers = {};

        // Abort requests
        Request.Abort("call-join");
        Request.Abort("call-screen-share");

        this.selfId = "";

        if (this.selfUserStream) {
            UserMediaController.stopStream(this.selfUserStream);
            this.selfUserStream = null;
        }

        if (this.selfUserStreamAudio) {
            UserMediaController.stopStream(this.selfUserStreamAudio);
            this.selfUserStreamAudio = null;
        }

        this.dummyVideoTrack = null;

        // Stop and release silent-audio fallback resources if active.
        if (this.silentOscillator) {
            try { this.silentOscillator.stop(); } catch (_) { /**/ }
            this.silentOscillator = null;
        }
        if (this.silentCtx) {
            try { this.silentCtx.close(); } catch (_) { /**/ }
            this.silentCtx = null;
        }

        if (this.screenStream) {
            UserMediaController.stopStream(this.screenStream);
            this.screenStream = null;
        }

        this.sharingScreen = false;
        this.sharingScreenSelf = false;
        this.screenShareStream = null;
        this.screenShareData = {};

        if (!this.breakoutSwitching) {
            this.emit("join-code-change", "");
        }

        this.emit("joining", false);
    }

    public static rejoinCall() {
        if (this.closed) {
            return;
        }
        this.emit("joining", true);
        if (this.rejoinTimeout) {
            clearTimeout(this.rejoinTimeout);
            this.rejoinTimeout = null;
        }
        this.rejoinTimeout = setTimeout(() => {
            this.rejoinTimeout = null;
            this.joinCall(this.breakoutDesiredCode !== null ? this.breakoutDesiredCode : this.rejoinCode, (err) => {
                if (err) {
                    //TODO:
                    //App.showMessage($t("Error"), err.message);
                }
            });
        }, 2000);
    }

    public static joinCall(code, callback?) {
        if (typeof code === "function") {
            callback = code;
            code = "";
        }
        if (typeof callback !== "function") {
            callback = () => { /* Forced transitions do not need a UI callback. */ };
        }

        this.closed = false;

        this.rejoinCode = code;

        if (this.joinedCall) {
            // Mark the replacement as pending before sending call-leave. The
            // old LEFT acknowledgement must be ignored until the new
            // call-open arrives.
            if (this.callOpen) {
                this.pendingReplacementCallIds.push(this.callId);
            }
            this.callOpen = false;
            this.callId = "";
            this.callScopeId = "";
            // A forced workroom transition can ignore the old call-close
            // acknowledgement. Stop the old recorder explicitly so the new
            // call creates a fresh translation session.
            this.stopTranslation();
            this.leaveCall(undefined, this.breakoutSwitching);
            // A replacement call is a real leave/rejoin from the views'
            // perspective.  Clear the old participant mesh before the new
            // PeerJS connection is opened.
            this.emit("close", {
                preserveRecording: this.breakoutSwitching,
                preserveBreakoutPanel: this.breakoutSwitching,
                preserveSubtitles: true,
            });
        }
        this.destroyCallData();
        // leaveCall() marks the controller closed because it is also used by
        // the explicit leave action.  A join that is replacing an existing
        // call must reopen the controller before creating the new PeerJS peer.
        this.closed = false;

        this.emit("joining", true);

        if (!WebSocketController.room) {
            this.emit("joining", false);
            return callback(null);
        }

        this.audioMode = AppPreferences.AudioMode;

        Request.Pending("call-join", ApiCalls.GetRoomRoomidCall(WebSocketController.room, {}))
            .onSuccess((response) => {
                const peerConfig = {
                    host: response.configuration.peerjs.host,
                    port: response.configuration.peerjs.port,
                    secure: location.protocol === "https:",
                    key: response.configuration.peerjs.key,
                    config: response.configuration.rtc,
                };

                // Request.Pending("call-leave-time", ApiCalls.GetRoomRoomidCallLeavetime(WebSocketController.room))
                //     .onSuccess((response) => {
                //         this.leaveTime = response.time;
                //     })

                const micBaseConstraints = this.buildAudioInputConstraints();

                // If a preferred input device was saved, try it first.
                // If the device disappeared (USB unplugged/webcam changed),
                // transparently retry with the browser default microphone.
                const preferredAudioDeviceId = this.getPreferredAudioInputDevice();
                const requestMicStream = (forceDefaultDevice: boolean = false): Promise<MediaStream> => {
                    const audioConstraints: MediaTrackConstraints = {
                        ...micBaseConstraints,
                    };

                    if (preferredAudioDeviceId && !forceDefaultDevice) {
                        audioConstraints.deviceId = { exact: preferredAudioDeviceId };
                    }

                    return navigator.mediaDevices.getUserMedia({
                        video: false,
                        audio: audioConstraints,
                    }).catch((err) => {
                        const missingPreferredDevice = preferredAudioDeviceId && !forceDefaultDevice
                            && (err?.name === "NotFoundError" || err?.name === "OverconstrainedError");
                        if (missingPreferredDevice) {
                            console.warn("Preferred microphone not available, retrying with default input device:", err);
                            return requestMicStream(true);
                        }
                        throw err;
                    });
                };

                requestMicStream()
                    .then(async (stream) => {
                        this.video = false; // No video on joining
                        // Mark microphone as available — reset after any previous failure.
                        CallsController.micAvailable = true;

                        // Crear un nuevo peer
                        const peer = new Peer(peerConfig);
                        this.peer = peer;

                        let socketOpen = false;
                        // Ignore a close from a superseded PeerJS instance.
                        // Forced breakout transitions intentionally destroy the
                        // previous peer while the replacement is starting.
                        peer.on('close', () => {
                            if (this.peer === peer) this.onSocketDisconnected();
                        });
                        peer.on('disconnected', () => {
                            if (this.peer === peer) this.onSocketDisconnected();
                        });

                        peer.on('call', this.onCall.bind(this));

                        peer.on('error', (err) => {
                            if (this.peer !== peer) return;
                            if (!socketOpen) {
                                this.emit("joining", false);
                                if (this.fallbackFromBreakoutFailure()) {
                                    return callback(null, "");
                                }
                                this.rejoinCall();
                                return callback(null, "");
                            }
                            console.error(err);
                        });
                        peer.on("open", async (id) => {
                            this.peerID = id;
                            socketOpen = true;

                            this.joinedCall = true;
                            // Clone audio tracks for the AudioContext/MediaRecorder pipeline so
                            // the original track flows freely to WebRTC peer connections.  On
                            // some browsers, connecting a track to a suspended AudioContext via
                            // createMediaStreamSource() can starve other consumers (like
                            // RTCPeerConnection) of audio frames.
                            const localAudioTracks = stream.getAudioTracks().map(t => t.clone());
                            const localAudioStream = new MediaStream(localAudioTracks);
                            try {
                                this.audioContext = new AudioContext();
                                // Explicitly resume — browsers create AudioContext in suspended
                                // state unless triggered by a direct user gesture.
                                await this.audioContext.resume();
                                this.destination = CallsController.audioContext.createMediaStreamDestination();
                                this.mediaRecorder = new MediaRecorder(CallsController.destination.stream);
                                const localSource = CallsController.audioContext.createMediaStreamSource(localAudioStream);
                                localSource.connect(CallsController.destination);
                            } catch (audioErr) {
                                console.warn("[call] AudioContext/MediaRecorder setup failed, continuing without recording pipeline:", audioErr);
                            }
                            if (!this.joinedCall) {
                                this.emit("joining", false);
                                return callback(new Error($t("Cannot join the call.")));
                            }
                            // Unirse a la llamada con el usuario local
                            this.selfUserStream = stream;
                            this.selfUserStreamAudio = UserMediaController.removeVideoFromStream(stream);

                            // Add a muted 2x2 black canvas video track to selfUserStream.
                            // Having a video track in the initial SDP offer means every peer
                            // connection has a video RTCRtpSender from the start.  When the user
                            // enables their camera later, replaceTrack() can swap the dummy for
                            // the real track without requiring SDP renegotiation (which PeerJS
                            // does not perform after the initial offer/answer exchange).
                            try {
                                const _canvas = document.createElement('canvas');
                                _canvas.width = 2; _canvas.height = 2;
                                _canvas.getContext('2d')?.fillRect(0, 0, 2, 2);
                                const _dummyStream = (_canvas as any).captureStream(1) as MediaStream;
                                const _dummyTrack = _dummyStream.getVideoTracks()[0];
                                if (_dummyTrack) {
                                    _dummyTrack.enabled = false;
                                    this.selfUserStream.addTrack(_dummyTrack);
                                    this.dummyVideoTrack = _dummyTrack;
                                }
                            } catch (ex) {
                                console.error("Failed to create dummy video track:", ex);
                            }

                            // Apply options to stream
                            // NOTE: do NOT call stopVideo here — the dummy track must stay in the
                            // stream so that peer connections always have a video sender.  The
                            // track is already disabled (enabled=false) so it sends black frames.
                            UserMediaController.setMediaStreamAudioEnabled(this.selfUserStream, this.audio);
                            UserMediaController.setMediaStreamAudioEnabled(this.selfUserStreamAudio, this.audio);
                            WebSocketController.send({
                                type: "call-join",
                                peer_id: id,
                                call_code: code,
                                video: this.video,
                                audio: this.audio,
                                audio_mode: this.audioMode,
                                serializations: ["binary", "binary-utf8", "json"],
                                // date: date,
                            });
                            this.emit("joining", false);
                            this.emit("joined");
                            return callback(null, id);
                        })
                    })
                    .catch((err) => {
                        console.warn("Microphone not available, joining with silent audio:", err);
                        // Create a silent audio stream so the user can still join the call
                        try {
                            const silentCtx = new AudioContext();
                            const oscillator = silentCtx.createOscillator();
                            const gain = silentCtx.createGain();
                            gain.gain.value = 0; // completely silent
                            oscillator.connect(gain);
                            const dest = silentCtx.createMediaStreamDestination();
                            gain.connect(dest);
                            oscillator.start();
                            // Store so destroyCallData() can stop and close them.
                            CallsController.silentCtx = silentCtx;
                            CallsController.silentOscillator = oscillator;
                            const silentStream = dest.stream;
                            CallsController.micAvailable = false;
                            this.emit("warning-no-microphone");
                            // Continue the normal join flow with the silent stream
                            this.video = false;
                            const peer = new Peer(peerConfig);
                            this.peer = peer;

                            let socketOpen = false;
                            peer.on('close', () => {
                                if (this.peer === peer) this.onSocketDisconnected();
                            });
                            peer.on('disconnected', () => {
                                if (this.peer === peer) this.onSocketDisconnected();
                            });
                            peer.on('call', this.onCall.bind(this));
                            peer.on('error', (peerErr) => {
                                if (this.peer !== peer) return;
                                if (!socketOpen) {
                                    this.emit("joining", false);
                                    if (this.fallbackFromBreakoutFailure()) {
                                        return callback(null, "");
                                    }
                                    this.rejoinCall();
                                    return callback(null, "");
                                }
                                console.error(peerErr);
                            });
                            peer.on("open", async (id) => {
                                this.peerID = id;
                                socketOpen = true;
                                this.joinedCall = true;

                                // Clone tracks for the AudioContext pipeline (see main path).
                                const localAudioTracks = silentStream.getAudioTracks().map(t => t.clone());
                                const localAudioStream = new MediaStream(localAudioTracks);
                                try {
                                    this.audioContext = new AudioContext();
                                    await this.audioContext.resume();
                                    this.destination = CallsController.audioContext.createMediaStreamDestination();
                                    this.mediaRecorder = new MediaRecorder(CallsController.destination.stream);
                                    const localSource = CallsController.audioContext.createMediaStreamSource(localAudioStream);
                                    localSource.connect(CallsController.destination);
                                } catch (audioErr) {
                                    console.warn("[call] AudioContext/MediaRecorder setup failed in silent path, continuing:", audioErr);
                                }

                                if (!this.joinedCall) {
                                    this.emit("joining", false);
                                    return callback(new Error($t("Cannot join the call.")));
                                }

                                this.selfUserStream = silentStream;
                                this.selfUserStreamAudio = UserMediaController.removeVideoFromStream(silentStream);

                                // Add dummy video track
                                try {
                                    const _canvas = document.createElement('canvas');
                                    _canvas.width = 2; _canvas.height = 2;
                                    _canvas.getContext('2d')?.fillRect(0, 0, 2, 2);
                                    const _dummyStream = (_canvas as any).captureStream(1) as MediaStream;
                                    const _dummyTrack = _dummyStream.getVideoTracks()[0];
                                    if (_dummyTrack) {
                                        _dummyTrack.enabled = false;
                                        this.selfUserStream.addTrack(_dummyTrack);
                                        this.dummyVideoTrack = _dummyTrack;
                                    }
                                } catch (ex) {
                                    console.error("Failed to create dummy video track:", ex);
                                }

                                // Audio is muted since no real mic
                                this.audio = false;
                                UserMediaController.setMediaStreamAudioEnabled(this.selfUserStream, false);
                                UserMediaController.setMediaStreamAudioEnabled(this.selfUserStreamAudio, false);

                                WebSocketController.send({
                                    type: "call-join",
                                    peer_id: id,
                                    call_code: code,
                                    video: this.video,
                                    audio: this.audio,
                                    audio_mode: this.audioMode,
                                    serializations: ["binary", "binary-utf8", "json"],
                                });
                                this.emit("joining", false);
                                this.emit("joined");
                                return callback(null, id);
                            });
                        } catch (silentErr) {
                            console.error("Failed to create silent audio fallback:", silentErr);
                            this.emit("joining", false);
                            this.emit("error-microphone");
                            this.joinedCall = false;
                            this.destroyCallData();
                            return (new Error($t("The microphone could not be accessed.")));
                        }
                    });
            })
            .onRequestError((err, handleErr) => {
                handleErr(err, {
                    unauthorized: () => {
                        if (this.fallbackFromBreakoutFailure()) {
                            return callback(null, "");
                        }
                        return callback(new Error(("Unauthorized: Your session token expired. Please, refresh the page to log back in.")));
                    },
                    forbidden: () => {
                        if (this.fallbackFromBreakoutFailure()) {
                            return callback(null, "");
                        }
                        return callback(new Error(("Access denied.")));
                    },
                    notFound: () => {
                        if (this.fallbackFromBreakoutFailure()) {
                            return callback(null, "");
                        }
                        return callback(new Error(("The room you tried to join was not found.")));
                    },
                    temporalError: () => {
                        if (this.fallbackFromBreakoutFailure()) {
                            return callback(null, "");
                        }
                        this.rejoinCall();
                        return callback(null, "");
                    },
                });
            })
            .onUnexpectedError(() => {
                if (this.fallbackFromBreakoutFailure()) {
                    return callback(null, "");
                }
                this.rejoinCall();
                return callback(null, "");
            });
    }


    public static startTranslation() {
        if (this.mediaRecorderTranslation) return;
        if (!this.joinedCall || !this.callOpen || !RoomController.subtitlesActive) return;
        if (!this.selfUserStreamAudio) return;

        // Use the already-acquired mic stream from joinCall — no need for a
        // second getUserMedia call (which would create an orphaned stream that
        // is never stopped, causing a double mic indicator in the browser and
        // potential audio track conflicts).
        try {
            const recorder = new MediaRecorder(this.selfUserStreamAudio, { mimeType: 'audio/webm' });
            this.mediaRecorderTranslation = recorder;
            recorder.ondataavailable = async (event) => {
                if (this.mediaRecorderTranslation !== recorder || !this.joinedCall || !this.callOpen
                    || !RoomController.subtitlesActive) {
                    return;
                }
                if (event.data.size > BLOB_SIZE_THRESHOLD) {
                    WebSocketController.sendBlob(event.data);
                }
            };

            recorder.start(250);
            WebSocketController.send({
                "type": "start-translation",
                "lan": this.lan,
                "room": WebSocketController.room,
            });
        } catch (err) {
            console.error("[startTranslation] Failed to start MediaRecorder:", err);
        }
    }

    public static changeLanguage() {
        WebSocketController.send({
            "type": "change-language",
            "lan": this.lan,
        })
    }

    public static stopTranslation() {
        const recorder = this.mediaRecorderTranslation;
        if (!recorder) return;

        this.mediaRecorderTranslation = null;

        try {
            recorder.stop();

            // Do NOT stop the underlying stream tracks here — the
            // MediaRecorder was created with selfUserStreamAudio, which
            // shares the same audio MediaStreamTrack used by all WebRTC
            // peer connections. Stopping them would kill outgoing audio.

            // Only stop the audio tracks when the call itself is ending.
            if (!this.joinedCall && this.selfUserStreamAudio) {
                this.selfUserStreamAudio.getTracks().forEach(track => track.stop());
                this.selfUserStreamAudio = null;
            }

            WebSocketController.send({
                type: "stop-translation",
            });
        } catch (error) {
            console.error("Error stopping translation:", error);

            try {
                if (recorder.state !== "inactive") {
                    recorder.stop();
                }
            } catch (e) {
                console.error("Error stopping MediaRecorder:", e);
            }

            if (!this.joinedCall && this.selfUserStreamAudio) {
                try {
                    this.selfUserStreamAudio.getTracks().forEach(track => track.stop());
                } catch (e) {
                    console.error("Error stopping audio tracks:", e);
                }
                this.selfUserStreamAudio = null;
            }

            WebSocketController.send({
                type: "stop-translation",
            });
        }
    }
    public static startRecording(uid: string, recordId: string) {
        // Use the already-acquired mic stream — no need for another getUserMedia
        // call that would create an orphaned, never-closed stream.
        this.recording = true;
        this.adminRecord = RoomController.recordingUser = uid;

        // Iniciar grabación del audio local
        this.startRecordingForUser(this.selfId, this.selfUserStreamAudio, recordId);
        this.askRecording(this.room, uid);

        // Iniciar grabación para cada participante remoto
        for (const participantId in this.callUsers) {
            if (this.callUsers[participantId].stream) {
                const audioTracks = this.callUsers[participantId].stream.getAudioTracks();
                if (audioTracks.length > 0) {
                    const audioStream = new MediaStream(audioTracks);
                    this.startRecordingForUser(participantId, audioStream, recordId);
                }
            }
        }
    }

    public static startRecordingForUser(participantId, stream, recordId) {
        if (!stream || !recordId) {
            return;
        }

        const mediaRecorder = new MediaRecorder(stream, {
            mimeType: 'audio/webm;codecs=opus',
        });
        this.mediaRecorders[participantId] = mediaRecorder;
        this.audioChunksPerUser[participantId] = [];

        mediaRecorder.start(2000); // Capturar chunks cada 2 segundos


        // Crear el AudioContext y el analizador para detectar el silencio
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        const analyser = audioContext.createAnalyser();
        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);
        const bufferLength = analyser.fftSize;
        const dataArray = new Uint8Array(bufferLength);

        const lastSpokenTimestamp = null;
        const silenceThreshold = -20; // Ajusta según la sensibilidad deseada
        const silenceDuration = 2000; // 2 segundo de silencio para marcar el final de una frase


        mediaRecorder.ondataavailable = event => {
            if (event.data.size > BLOB_SIZE_THRESHOLD) {
                const now = Date.now();
                const file = new File([event.data], now + "_" + participantId + ".webm", { type: "audio/webm" });
                Request.Pending("upload-audio", ApiCalls.UploadAudioChunk(WebSocketController.room, recordId, {
                    file: file,
                    joinRecordTimestamp: "" + this.joinRecordTimestamp,
                })).onSuccess(() => {
                })
                    .onRequestError((err, handleErr) => {
                        handleErr(err, {
                            badRequest: () => {
                                console.error("Bad request.");
                            },
                            badRequestInvalidFile: () => {
                                console.error("Invalid file.");
                            },
                            badRequestInvalidJoinRecordTimestamp: () => {
                                console.error("Invalid join record timestamp.");
                            },
                            badRequestCallNotRecording: () => {
                                console.error("The call is not being recorded.");
                            },
                            forbidden: () => {
                                console.error("Access denied.");
                            },
                            notFound: () => {
                                console.error("The room you tried to join was not found.");
                            },
                            notFoundCallNotFound: () => {
                                console.error("The call you tried to join was not found.");
                            },
                            notFoundRecordNotFound: () => {
                                console.error("The record you tried to join was not found.");
                            },
                            notFoundRoomNotFound: () => {
                                console.error("The room you tried to join was not found.");
                            },
                            temporalError: () => {
                                console.error("Cannot share screen due to a connection error.");
                            },
                        });
                    });
            }
        };
        this.joinRecordTimestamp = Date.now();
        const checkForSilence = () => {
            analyser.getByteTimeDomainData(dataArray);

            const rms = Math.sqrt(dataArray.reduce((sum, value) => {
                // Centrar en 0 (los valores vienen de 0-255, centro es 128)
                const v = (value - 128) / 128;
                return sum + (v * v);
            }, 0) / bufferLength);

            // Convertir a decibelios (evitando log de 0)
            const db = 20 * Math.log10(rms || 0.0000001);


            const now = Date.now() + this.leaveTime;
            if (db < silenceThreshold && lastSpokenTimestamp !== null && (now - lastSpokenTimestamp) > silenceDuration) {
                // Registrar el timestamp del final de la frase
                this.audioChunksPerUser[participantId].push({ timestamp: now, phraseEnd: true });
            }
        };

        // Repetir la comprobación de silencio en intervalos regulares
        const silenceInterval = setInterval(checkForSilence, 100);

        mediaRecorder.onstop = () => {
            clearInterval(silenceInterval);
            if (this.selfUserStream == stream && !this.breakoutSwitching) {
                Request.Pending("stop-call-record", ApiCalls.StopCallRecord(WebSocketController.room, recordId))
                    .onSuccess(() => {
                    })
                    .onRequestError((err, handleErr) => {
                        handleErr(err, {
                            badRequest: () => {
                                console.error("Bad request.");
                            },
                            badRequestCallNotRecording: () => {
                                console.error("The call is not being recorded.");
                            },
                            forbidden: () => {
                                console.error("Access denied.");
                            },
                            forbiddenNotRecordAdmin: () => {
                                console.error("You are not the admin of the call.");
                            },
                            notFound: () => {
                                console.error("The room you tried to join was not found.");
                            },
                            notFoundCallNotFound: () => {
                                console.error("The call you tried to join was not found.");
                            },
                            notFoundRoomNotFound: () => {
                                console.error("The room you tried to join was not found.");
                            },
                            unauthorized: () => {

                            }, temporalError: () => {
                                console.error("Cannot share screen due to a connection error.");
                            }
                        });
                    });
                clearInterval(silenceInterval);
            }
        }
    }

    public static async leaveCall(cont?: () => void, preserveRecording: boolean = false) {
        this.closed = true;
        if (this.joinedCall) {
            if (this.recording && this.adminRecord === this.selfId) {
                await this.criticalSection.acquire();
                this.criticalSection.release();
            }
            RoomController.onLeaveCall();
            if (cont && typeof cont === "function") {
                cont();
            }
            WebSocketController.send({
                "type": "call-leave",
                "finaltime": Date.now(),
                "breakout_transition": preserveRecording,
            });
        } else {
            this.destroyCallData();
        }
    }

    public static callPeer(uid: string, peerId: string, retryCount: number = 0) {
        const MAX_RETRIES = 3;
        const RETRY_DELAY = 1000 + (retryCount * 500); // Incrementar delay con cada retry

        // Verificar que el usuario sigue en la llamada
        if (!this.joinedCall || !this.callUsers[uid]) {
            return;
        }

        const options = {
            'constraints': {
                'mandatory': {
                    'OfferToReceiveAudio': true,
                    'OfferToReceiveVideo': true
                },
                offerToReceiveAudio: 1,
                offerToReceiveVideo: 1,
            }
        };

        if (this.callUsers[uid].call) {
            try {
                this.callUsers[uid].call.removeAllListeners();
                this.callUsers[uid].call.close();
            } catch (ex) {
                console.error(ex);
            }
        }

        const streamForCall = (this.audioMode || this.callUsers[uid].audio_mode) ? this.selfUserStreamAudio : this.selfUserStream;


        // Verificar que tenemos un stream válido
        if (!streamForCall) {
            console.error(`No stream available for callPeer ${uid}`);
            return;
        }

        const call = this.peer.call(peerId, streamForCall, options);
        this.callUsers[uid].call = call;

        call.on("stream", (stream) => {
            // Ignore if this call was superseded by a newer one
            if (!this.callUsers[uid] || this.callUsers[uid].call !== call) return;
            this.callUsers[uid].stream = stream;
            this.resumeRecordingForParticipant(uid, stream);
            this.emit("user-stream", uid, stream);
        });

        // Single flag shared by error + close to prevent double-retry
        let callClosed = false;

        const handleCallEnd = (isError: boolean, err?: any) => {
            if (callClosed) return;
            callClosed = true;
            if (isError) console.error(err);

            // Only retry if: this call is still the active one for this uid,
            // the user is still present, and we haven't exceeded the retry limit.
            if (
                retryCount < MAX_RETRIES &&
                this.joinedCall &&
                this.callUsers[uid] &&
                this.callUsers[uid].peerId === peerId &&
                this.callUsers[uid].call === call
            ) {
                setTimeout(() => {
                    if (
                        this.joinedCall &&
                        this.callUsers[uid] &&
                        this.callUsers[uid].peerId === peerId &&
                        this.callUsers[uid].call === call
                    ) {
                        this.callPeer(uid, peerId, retryCount + 1);
                    }
                }, RETRY_DELAY);
            } else if (retryCount >= MAX_RETRIES) {
                console.error(`Max retries reached for callPeer ${uid} or user no longer available`);
            }
        };

        call.on("error", (err) => handleCallEnd(true, err));
        call.on("close", () => handleCallEnd(false));
    }

    public static callPeerScreen(uid, peerId) {
        const call = this.screenPeer.call(peerId, this.screenStream, { metadata: "screen" });

        if (this.callUsers[uid].screenCall) {
            try {
                this.callUsers[uid].screenCall.removeAllListeners();
                this.callUsers[uid].screenCall.close();
            } catch (ex) {
                console.error(ex);
            }
        }

        this.callUsers[uid].screenCall = call;

        let callClosed = false;

        this.callUsers[uid].screenCall.on("error", (err) => {
            console.error(err);
            // console.log("[Screen Share] Peer disconnected: " + peerId + " / User: " + uid);

            if (callClosed) {
                return;
            }
            callClosed = true;

            setTimeout(() => {
                if (this.screenPeer && this.screenStream && this.callUsers[uid] && this.callUsers[uid].screenCall === call && this.callUsers[uid].peerId === peerId) {
                    this.callPeerScreen(uid, peerId);
                }
            }, 1000);
        });

        this.callUsers[uid].screenCall.on("close", () => {
            // console.log("[Screen Share] Peer disconnected: " + peerId + " / User: " + uid);

            if (callClosed) {
                return;
            }
            callClosed = true;

            setTimeout(() => {
                if (this.screenPeer && this.screenStream && this.callUsers[uid] && this.callUsers[uid].screenCall === call && this.callUsers[uid].peerId === peerId) {
                    this.callPeerScreen(uid, peerId);
                }
            }, 1000);
        });
    }

    public static startShareScreen(callback) {
        if (this.screenPeer) {
            return;
        }

        Request.Pending("call-screen-share", ApiCalls.GetRoomRoomidCall(WebSocketController.room, {}))
            .onSuccess((response) => {
                const peerConfig = {
                    host: response.configuration.peerjs.host,
                    port: response.configuration.peerjs.port,
                    secure: location.protocol === "https",
                    key: response.configuration.peerjs.key,
                    config: response.configuration.rtc,
                };

                // Create new peer
                this.screenPeer = new Peer(peerConfig);

                let socketOpen = false;

                this.screenPeer.on("open", (id) => {
                    this.screenPeerId = id;
                    socketOpen = true;

                    UserMediaController.getDisplayMedia((stream, ex) => {
                        if (!this.joinedCall) {
                            // Left the call in the middle of join
                            if (this.screenPeer) {
                                this.screenPeer.destroy();
                                this.screenPeer = null;
                            }
                            return callback(new Error(("Left the call while sharing screen.")));
                        }

                        if (!stream) {
                            if (this.screenPeer) {
                                this.screenPeer.destroy();
                                this.screenPeer = null;
                            }
                            if (ex && (ex + "").toLowerCase().indexOf("by system") >= 0) {
                                //TODO:
                                //App.$refs.shareScreenErrorModal.show();
                            }
                            return callback(null);
                        }

                        // Get the stream and join the call
                        this.screenStream = stream;
                        WebSocketController.send({
                            type: "call-share-screen-start",
                            peer_id: id,
                        });

                        return callback(null, id);
                    });
                });

                this.peer.on('error', (err) => {
                    if (!socketOpen) {
                        return callback(new Error(("Fatal error") + ": " + err.message));
                    }
                    console.error(err);
                });
            })
            .onRequestError((err, handleErr) => {
                handleErr(err, {
                    unauthorized: () => {
                        return callback(new Error(("Unauthorized: Your session token expired. Please, refresh the page to log back in.")));
                    },
                    forbidden: () => {
                        return callback(new Error(("Access denied.")));
                    },
                    notFound: () => {
                        return callback(new Error(("The room you tried to join was not found.")));
                    },
                    temporalError: () => {
                        return callback(new Error(("Cannot share screen due to a connection error.")));
                    },
                });
            })
            .onUnexpectedError((err) => {
                return callback(new Error(("Unexpected error. Error message: ") + err.message));
            });
    }

    public static endShareScreen() {
        if (!this.screenPeer) {
            return;
        }

        WebSocketController.send({
            type: "call-share-screen-end"
        });
    }

    // Call Record

    public static adminRecord = "";
    public static recordID = "";
    public static mediaRecorder = null;
    public static joinRecordTimestamp = 0;

    private static resumeRecordingForParticipant(participantId: string, stream: MediaStream) {
        if (!this.recording || !this.recordID || !this.adminRecord || this.adminRecord !== this.selfId || !stream
            || stream.getAudioTracks().length === 0) {
            return;
        }

        const currentRecorder = this.mediaRecorders[participantId];
        if (currentRecorder && currentRecorder.state === "recording" && currentRecorder.stream === stream) {
            return;
        }

        if (currentRecorder && currentRecorder.state === "recording") {
            try {
                currentRecorder.stop();
            } catch (ex) {
                console.error("Error replacing call recording stream:", ex);
            }
        }

        this.startRecordingForUser(participantId, stream, this.recordID);
    }

    private static resumeRecordingForCurrentCall() {
        if (!this.recording || !this.recordID || this.adminRecord !== this.selfId) {
            return;
        }

        this.resumeRecordingForParticipant(this.selfId, this.selfUserStreamAudio);
        for (const participantId of Object.keys(this.callUsers)) {
            const stream = this.callUsers[participantId]?.stream;
            if (stream) {
                this.resumeRecordingForParticipant(participantId, stream);
            }
        }
    }

    public static async stopRecord(recordId: string, uid: string) {
        if (this.adminRecord !== uid) {
            console.error("You are not the admin of the call.");
            return;
        }
        this.recording = false;
        for (const participantId in this.mediaRecorders) {
            const recorder = this.mediaRecorders[participantId];
            if (recorder && recorder.state === "recording") {
                recorder.stop();
                if (recorder.stream == this.selfUserStreamAudio) {
                    // recorder.stream.getTracks().forEach(track => track.stop());
                    recorder.onstop = () => {
                        Request.Pending("stop-call-record", ApiCalls.StopCallRecord(WebSocketController.room, recordId))
                            .onSuccess(() => {
                                this.criticalSection.release();
                            })
                            .onRequestError((err, handleErr) => {
                                handleErr(err, {
                                    badRequest: () => {
                                        console.error("Bad request.");
                                    },
                                    badRequestCallNotRecording: () => {
                                        console.error("The call is not being recorded.");
                                    },
                                    forbidden: () => {
                                        console.error("Access denied.");
                                    },
                                    forbiddenNotRecordAdmin: () => {
                                        console.error("You are not the admin of the call.");
                                    },
                                    notFound: () => {
                                        console.error("The room you tried to join was not found.");
                                    },
                                    notFoundCallNotFound: () => {
                                        console.error("The call you tried to join was not found.");
                                    },
                                    notFoundRoomNotFound: () => {
                                        console.error("The room you tried to join was not found.");
                                    },
                                    unauthorized: () => {

                                    }, temporalError: () => {
                                        console.error("Cannot share screen due to a connection error.");
                                    }
                                });
                            })
                    }
                }
            }
        }
    }

    public static checkInCall() {
        if (this.closed) {
            return;
        }
        if (this.selfUserStream) {
            const userStreams = Object.keys(this.callUsers).map(uid => ({
                id: uid,
                stream: this.callUsers[uid].stream,
                userInfo: {
                    name: this.callUsers[uid].userInfo.name,
                    image: this.callUsers[uid].userInfo.image,
                },
                muted: this.callUsers[uid].muted,
                video: this.callUsers[uid].video,
                audio: this.callUsers[uid].audio,
                hand: this.callUsers[uid].hand,
            }));
            // Add self user to the list
            userStreams.push({
                id: this.selfId,
                stream: this.selfUserStream,
                userInfo: {
                    name: this.selfName,
                    image: this.selfImage,
                },
                muted: !this.audio,
                video: this.video,
                audio: this.audio,
                hand: this.hand,
            });
            return { userStreams };
        }
        return null;
    }

    public static videoAvailable(stream) {
        this.emit("video-available", stream);
    }
}
